// assets/ud-fundraiser.js
import { CartUpdateEvent } from '@theme/events';

const KEY = 'ud_fundraiser';
const ATTR = { id: 'Seller ID', handle: 'Fundraiser Handle', title: 'Fundraiser Title' };

// localStorage is only a mirror. Cart attributes are the source of truth, because
// Safari caps all script-writable storage at 7 days and can throw outright in
// private browsing. Every access is guarded so a throw never breaks the page.
const mirror = {
  read() {
    try {
      return JSON.parse(localStorage.getItem(KEY) || 'null');
    } catch {
      return null;
    }
  },
  write(value) {
    try {
      localStorage.setItem(KEY, JSON.stringify(value));
    } catch {
      /* private browsing, quota, disabled — the cart attribute still carries it */
    }
  },
};

const cartJson = () => fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());

async function applyAttributes({ id, handle, title }) {
  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .join(',');

  const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: { [ATTR.id]: id, [ATTR.handle]: handle, [ATTR.title]: title },
      sections,
      sections_url: window.location.pathname,
    }),
  });

  const cart = await response.json();
  mirror.write({ id, handle, title });
  return cart;
}

// --- Capture: stamp the fundraiser onto the cart when the hero page is viewed ---
async function capture(source) {
  const next = {
    id: source.dataset.udSellerId || '',
    handle: source.dataset.udFundraiserHandle || '',
    title: source.dataset.udFundraiserTitle || '',
  };
  if (!next.id) return;

  try {
    const cart = await cartJson();
    const current = cart.attributes || {};
    const unchanged =
      current[ATTR.id] === next.id && current[ATTR.handle] === next.handle && current[ATTR.title] === next.title;

    // Mirror regardless, but skip the write when nothing would change.
    mirror.write(next);
    if (unchanged) return;

    await applyAttributes(next);
  } catch (error) {
    console.error(error);
  }
}

const NOT_FOUND = 'Seller ID not found. Please try again.';
const MAP_KEY = 'ud_seller_map';

// --- Lookup: match against the index Liquid embedded in the drawer ---
// Liquid's `pages` object paginates at 50 per render and honours the `page` URL
// parameter, so on /collections/x?page=2 the embedded index would be offset and
// come back empty. Keeping the last non-empty copy makes that harmless.
function sellerIndex(root) {
  let list = [];
  try {
    list = JSON.parse(root.querySelector('[data-ud-seller-map]')?.textContent || '[]');
  } catch (error) {
    console.error('[ud-fundraiser] Could not parse the seller index:', error);
  }

  if (list.length) {
    try {
      localStorage.setItem(MAP_KEY, JSON.stringify(list));
    } catch {
      /* storage unavailable — the embedded index still works on normal pages */
    }
    return list;
  }

  try {
    return JSON.parse(localStorage.getItem(MAP_KEY) || '[]');
  } catch {
    return [];
  }
}

function findSeller(root, id) {
  const wanted = id.trim().toLowerCase();
  return sellerIndex(root).find((entry) => String(entry.id).trim().toLowerCase() === wanted) || null;
}

// --- Apply: validate a hand-typed Seller ID, then attach it ---
async function applyTypedSellerId(root) {
  const input = root.querySelector('[data-ud-seller-input]');
  const button = root.querySelector('[data-ud-seller-apply]');
  const error = root.querySelector('[data-ud-seller-error]');
  if (!input || !button) return;

  const value = input.value.trim();
  const fail = (message) => {
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
    input.setAttribute('aria-invalid', 'true');
    input.focus();
    input.select();
  };

  if (error) error.hidden = true;
  input.removeAttribute('aria-invalid');

  if (!value) return fail('Please enter a Seller ID.');

  const match = findSeller(root, value);
  if (!match) return fail(NOT_FOUND);

  button.disabled = true;
  try {
    const cart = await applyAttributes({ id: match.id, handle: match.handle, title: match.title });
    root.dispatchEvent(
      new CartUpdateEvent(cart, 'ud-fundraiser', {
        source: 'ud-fundraiser',
        itemCount: cart.item_count,
        sections: cart.sections,
      })
    );
  } catch (err) {
    // Only reachable if writing the cart attribute fails — the match already succeeded.
    console.error('[ud-fundraiser]', err);
    button.disabled = false;
    fail('Something went wrong. Please try again.');
  }
}

// --- Restore: an emptied or expired cart loses its attributes; put them back ---
async function restore(root) {
  const saved = mirror.read();
  if (!saved?.id) return;

  try {
    const cart = await cartJson();
    if ((cart.attributes || {})[ATTR.id]) return;

    const updated = await applyAttributes(saved);
    root.dispatchEvent(
      new CartUpdateEvent(updated, 'ud-fundraiser', {
        source: 'ud-fundraiser',
        itemCount: updated.item_count,
        sections: updated.sections,
      })
    );
  } catch (error) {
    console.error(error);
  }
}

// --- Clear every non-bundle line in one request, then follow the link if there is one ---
async function clearLoose(trigger) {
  const keys = (trigger.dataset.udClearLoose || '').split(',').filter(Boolean);
  const href = trigger.getAttribute('href');
  const go = () => {
    if (href) window.location.href = href;
  };

  if (!keys.length) return go();

  trigger.setAttribute('aria-disabled', 'true');

  try {
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean)
      .join(',');

    const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: Object.fromEntries(keys.map((key) => [key, 0])),
        sections,
        sections_url: window.location.pathname,
      }),
    });
    const cart = await response.json();

    if (href) return go();

    trigger.removeAttribute('aria-disabled');
    trigger.dispatchEvent(
      new CartUpdateEvent(cart, 'ud-fundraiser', {
        source: 'ud-fundraiser',
        itemCount: cart.item_count,
        sections: cart.sections,
      })
    );
  } catch (error) {
    console.error('[ud-fundraiser]', error);
    trigger.removeAttribute('aria-disabled');
    // Still navigate: being stranded in the cart is worse than an uncleared line.
    go();
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const clear = event.target.closest('[data-ud-clear-loose]');
  if (clear) {
    event.preventDefault();
    return clearLoose(clear);
  }

  const button = event.target.closest('[data-ud-seller-apply]');
  if (button) applyTypedSellerId(button.closest('[data-ud-cart-fundraiser]'));
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' || !(event.target instanceof Element)) return;
  if (!event.target.matches('[data-ud-seller-input]')) return;
  event.preventDefault();
  applyTypedSellerId(event.target.closest('[data-ud-cart-fundraiser]'));
});

function init() {
  const source = document.querySelector('[data-ud-seller-id]');
  if (source) capture(source);

  const root = document.querySelector('[data-ud-cart-fundraiser]');
  if (root && !source) restore(root);
}

init();
document.addEventListener('cart:update', (event) => {
  if (event.detail?.data?.source === 'ud-fundraiser') return;
  const root = document.querySelector('[data-ud-cart-fundraiser]');
  if (root && !document.querySelector('[data-ud-seller-id]')) restore(root);
});
